import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  PollType,
  ConstantSumCluster,
  SubCategory,
  RankingOption,
  SavedConfigurations,
  PollResultView,
  defaultResultViews,
  pollResultViewOptions,
} from "./pollTypes";
import { uploadResizedImage } from "@/lib/imageUploader";
import { PollConstantSumEditor } from "./PollConstantSumEditor";
import { PollRankingEditor } from "./PollRankingEditor";
import { usePollEditorData } from "./hooks/usePollEditorData";

interface PollEditorProps {
  postId: string | null;
  published: boolean;
}

export interface PollEditorHandle {
  savePoll: (postIdOverride?: string) => Promise<{
    status: "saved" | "skipped" | "invalid" | "error";
  }>;
}

const pollTypeMeta: Record<PollType, { label: string; description: string }> = {
  constant_sum_two_level: {
    label: "2-level constant sum",
    description: "Allocate 100 points across categories and subcategories.",
  },
  ranking: {
    label: "Drag-and-drop ranking",
    description: "Let readers drag to order options from most to least preferred.",
  },
  ranking_minimum: {
    label: "Partial ranking",
    description: "Require readers to rank a minimum number of options while leaving the rest unranked.",
  },
};

export const PollEditor = forwardRef<PollEditorHandle, PollEditorProps>(({ postId, published }, ref) => {
  const [pollId, setPollId] = useState<string | null>(null);
  const [activePollType, setActivePollType] = useState<PollType>("constant_sum_two_level");
  const [clusters, setClusters] = useState<ConstantSumCluster[]>([]);
  const [hideZeroAllocated, setHideZeroAllocated] = useState(false);
  const [rankingOptions, setRankingOptions] = useState<RankingOption[]>([]);
  const [minimumRequiredRanks, setMinimumRequiredRanks] = useState(3);
  const [savedConfigurations, setSavedConfigurations] = useState<SavedConfigurations>({});
  const [resultViews, setResultViews] = useState<Record<PollType, PollResultView>>({ ...defaultResultViews });
  const [loading, setLoading] = useState(false);
  const [showWipeDialog, setShowWipeDialog] = useState(false);
  const [responseCount, setResponseCount] = useState(0);
  const [baselinePollType, setBaselinePollType] = useState<PollType | null>(null);
  const [baselineConfigurations, setBaselineConfigurations] = useState<SavedConfigurations>({});
  const [baselineSettings, setBaselineSettings] = useState<{
    hideZeroAllocated: boolean;
    minimumRequiredRanks: number;
    resultViews: Record<PollType, PollResultView>;
  } | null>(null);

  const { loadPoll, savePoll, wipePollData } = usePollEditorData({
    postId,
    pollId,
    activePollType,
    clusters,
    hideZeroAllocated,
    rankingOptions,
    minimumRequiredRanks,
    resultViews,
    responseCount,
    baselinePollType,
    baselineConfigurations,
    baselineSettings,
    setPollId,
    setActivePollType,
    setClusters,
    setHideZeroAllocated,
    setRankingOptions,
    setMinimumRequiredRanks,
    setSavedConfigurations,
    setResultViews,
    setResponseCount,
    setBaselinePollType,
    setBaselineConfigurations,
    setBaselineSettings,
    setLoading,
  });

  useEffect(() => {
    if (postId) {
      loadPoll();
    }
  }, [postId, loadPoll]);

  useEffect(() => {
    setMinimumRequiredRanks((prev) => {
      if (rankingOptions.length === 0) return prev;
      const capped = Math.min(prev, rankingOptions.length);
      return Math.max(1, capped);
    });
  }, [rankingOptions.length]);

  const addCluster = () => {
    if (clusters.length >= 10) {
      toast.error("Maximum 10 clusters allowed");
      return;
    }
    setClusters([...clusters, {
      label: "",
      description: "",
      image_url: "",
      order: clusters.length,
      subCategories: []
    }]);
  };

  const addRankingOption = () => {
    if (rankingOptions.length >= 15) {
      toast.error("Maximum 15 options allowed for ranking polls");
      return;
    }

    setRankingOptions((prev) => [
      ...prev,
      {
        label: "",
        description: "",
        image_url: "",
        order: prev.length,
      },
    ]);
  };

  const removeCluster = (index: number) => {
    setClusters(clusters.filter((_, i) => i !== index));
  };

  const removeRankingOption = (index: number) => {
    setRankingOptions((prev) => prev.filter((_, i) => i !== index));
  };

  const updateCluster = (index: number, field: keyof ConstantSumCluster, value: string) => {
    const updated = [...clusters];
    updated[index] = { ...updated[index], [field]: value };
    setClusters(updated);
  };

  const updateRankingOption = (index: number, field: keyof RankingOption, value: string) => {
    setRankingOptions((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleResultViewChange = (value: PollResultView) => {
    setResultViews((prev) => ({ ...prev, [activePollType]: value }));
  };

  const moveRankingOption = (from: number, to: number) => {
    setRankingOptions((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(from, 1);
      updated.splice(to, 0, moved);
      return updated.map((option, order) => ({ ...option, order }));
    });
  };

  const mapConfiguration = (from: PollType, to: PollType) => {
    const warnings: string[] = [];

    if (from === "constant_sum_two_level" && (to === "ranking" || to === "ranking_minimum")) {
      const mapped = clusters.map((cluster, index) => ({
        label: cluster.label,
        description: cluster.description,
        image_url: cluster.image_url,
        order: index,
      }));

      if (clusters.some((cluster) => cluster.subCategories.length > 0)) {
        warnings.push("Sub-categories are not used in ranking polls and will be ignored.");
      }

      if (mapped.length === 0) {
        warnings.push("No clusters available to map; the ranking poll will start empty.");
      }

      return { warnings, mappedRanking: mapped, minimumRequired: Math.min(3, mapped.length || 3) };
    }

    if ((from === "ranking" || from === "ranking_minimum") && to === "constant_sum_two_level") {
      const mapped = rankingOptions.map((option, index) => ({
        label: option.label,
        description: option.description,
        image_url: option.image_url,
        order: index,
        subCategories: [] as SubCategory[],
      }));

      warnings.push("Sub-categories will start empty when switching from ranking to constant-sum.");

      if (mapped.length === 0) {
        warnings.push("No ranking options are available to map.");
      }

      return { warnings, mappedClusters: mapped };
    }

    if (from === "ranking" && to === "ranking_minimum") {
      return { warnings, mappedRanking: rankingOptions, minimumRequired: Math.min(3, rankingOptions.length || 3) };
    }

    if (from === "ranking_minimum" && to === "ranking") {
      return { warnings, mappedRanking: rankingOptions };
    }

    return { warnings };
  };

  const handlePollTypeChange = (nextType: PollType) => {
    if (nextType === activePollType) return;

    const savedTarget = savedConfigurations[nextType];

    if (nextType === "constant_sum_two_level") {
      const savedClusters = savedTarget && "clusters" in savedTarget ? (savedTarget as { clusters: ConstantSumCluster[]; hideZeroAllocated?: boolean }) : null;
      if (savedClusters) {
        setClusters(savedClusters.clusters || []);
        setHideZeroAllocated(savedClusters.hideZeroAllocated ?? false);
        setActivePollType(nextType);
        return;
      }

      if (clusters.length > 0) {
        setActivePollType(nextType);
        return;
      }
    }

    if (nextType === "ranking") {
      const savedRanking = savedTarget && "options" in savedTarget ? (savedTarget as { options: RankingOption[] }) : null;
      if (savedRanking) {
        setRankingOptions(savedRanking.options || []);
        setActivePollType(nextType);
        return;
      }

      if (rankingOptions.length > 0) {
        setActivePollType(nextType);
        return;
      }
    }

    if (nextType === "ranking_minimum") {
      const savedRanking =
        savedTarget && "options" in savedTarget
          ? (savedTarget as { options: RankingOption[]; minimumRequired?: number })
          : null;

      if (savedRanking) {
        setRankingOptions(savedRanking.options || []);
        if (savedRanking.minimumRequired) {
          setMinimumRequiredRanks(Math.min(savedRanking.minimumRequired, Math.max(1, savedRanking.options.length)));
        }
        setActivePollType(nextType);
        return;
      }

      if (rankingOptions.length > 0) {
        setMinimumRequiredRanks((prev) => Math.min(Math.max(1, prev), rankingOptions.length));
        setActivePollType(nextType);
        return;
      }
    }

    const { warnings, mappedClusters, mappedRanking, minimumRequired } = mapConfiguration(activePollType, nextType);
    if (warnings.length > 0) {
      const message = warnings.join("\n");
      toast.warning(message);
      if (!confirm(`Switch poll type?\n\n${message}`)) {
        return;
      }
    }

    if (nextType === "constant_sum_two_level" && mappedClusters) {
      setClusters(mappedClusters);
      setHideZeroAllocated(false);
    }

    if (nextType === "ranking" && mappedRanking) {
      setRankingOptions(mappedRanking);
    }

    if (nextType === "ranking_minimum" && mappedRanking) {
      setRankingOptions(mappedRanking);
      setMinimumRequiredRanks(Math.min(minimumRequired ?? 3, Math.max(1, mappedRanking.length)));
    }

    setActivePollType(nextType);
  };

  const handleImageUpload = async (index: number, file: File) => {
    try {
      const url = await uploadResizedImage(file, {
        bucket: "poll-cluster-images",
        thresholdWidth: 250,
        targetWidth: 200,
      });
      updateCluster(index, "image_url", url);
      toast.success("Image uploaded successfully");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image");
    }
  };

  const handleRemoveImage = async (index: number, imageUrl?: string) => {
    if (imageUrl) {
      try {
        const path = imageUrl.split('/').pop();
        if (path) {
          const filesToRemove = [path];
          
          // If this is the small version, also remove original
          if (path.includes('-small')) {
            const originalPath = path.replace('-small', '');
            filesToRemove.push(originalPath);
          } else {
            // If this is the original, also remove small version
            const smallPath = path.replace(/\.(\w+)$/, '-small.$1');
            filesToRemove.push(smallPath);
          }
          
          await supabase.storage
            .from('poll-cluster-images')
            .remove(filesToRemove);
        }
      } catch (error) {
        console.error("Error removing image:", error);
      }
    }
    updateCluster(index, 'image_url', '');
  };

  const handleRankingImageUpload = async (index: number, file: File) => {
    try {
      const url = await uploadResizedImage(file, {
        bucket: "poll-cluster-images",
        thresholdWidth: 250,
        targetWidth: 200,
      });
      updateRankingOption(index, "image_url", url);
      toast.success("Image uploaded successfully");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image");
    }
  };

  const handleRankingRemoveImage = async (index: number, imageUrl?: string) => {
    if (imageUrl) {
      try {
        const path = imageUrl.split('/').pop();
        if (path) {
          const filesToRemove = [path];

          if (path.includes('-small')) {
            const originalPath = path.replace('-small', '');
            filesToRemove.push(originalPath);
          } else {
            const smallPath = path.replace(/\.(\w+)$/, '-small.$1');
            filesToRemove.push(smallPath);
          }

          await supabase.storage
            .from('poll-cluster-images')
            .remove(filesToRemove);
        }
      } catch (error) {
        console.error("Error removing image:", error);
      }
    }
    updateRankingOption(index, 'image_url', '');
  };

  const addSubCategory = (clusterIndex: number) => {
    const cluster = clusters[clusterIndex];
    if (cluster.subCategories.length >= 5) {
      toast.error("Maximum 5 sub-categories allowed per cluster");
      return;
    }
    const updated = [...clusters];
    updated[clusterIndex].subCategories.push({
      label: "",
      description: "",
      order: cluster.subCategories.length
    });
    setClusters(updated);
  };

  const removeSubCategory = (clusterIndex: number, subIndex: number) => {
    const updated = [...clusters];
    updated[clusterIndex].subCategories = updated[clusterIndex].subCategories.filter((_, i) => i !== subIndex);
    setClusters(updated);
  };

  const updateSubCategory = (clusterIndex: number, subIndex: number, field: keyof SubCategory, value: string) => {
    const updated = [...clusters];
    updated[clusterIndex].subCategories[subIndex] = {
      ...updated[clusterIndex].subCategories[subIndex],
      [field]: value
    };
    setClusters(updated);
  };

  useImperativeHandle(ref, () => ({
    savePoll,
  }));

  const handleWipePollData = async () => {
    const success = await wipePollData();
    if (success) {
      setShowWipeDialog(false);
    }
  };

  const activeResultView = resultViews[activePollType] ?? defaultResultViews[activePollType];
  const activeResultViewOptions = pollResultViewOptions[activePollType] ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Interactive Poll</span>
          <div className="flex items-center gap-2">
            {pollId && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => setShowWipeDialog(true)}
                disabled={loading}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Wipe Poll Data
              </Button>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent data-no-drag="true" className="space-y-4">
        {published && responseCount > 0 && (
          <div className="p-4 bg-warning/10 border border-warning rounded-lg flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-warning mt-0.5" />
            <div className="flex-1">
              <p className="font-medium">Published with {responseCount} responses</p>
              <p className="text-sm text-muted-foreground">Consider wiping poll data before republishing to reset results</p>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="pollType">Poll type</Label>
          <Select value={activePollType} onValueChange={(value) => handlePollTypeChange(value as PollType)}>
            <SelectTrigger id="pollType">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-background z-[100]">
              {Object.entries(pollTypeMeta).map(([value, meta]) => (
                <SelectItem key={value} value={value}>
                  <div>
                    <p className="font-medium leading-none">{meta.label}</p>
                    <p className="text-xs text-muted-foreground">{meta.description}</p>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="resultView">Results view</Label>
          <Select
            value={activeResultView}
            onValueChange={(value) => handleResultViewChange(value as PollResultView)}
            disabled={activeResultViewOptions.length === 0}
          >
            <SelectTrigger id="resultView">
              <SelectValue placeholder="Choose a results view" />
            </SelectTrigger>
            <SelectContent className="bg-background z-[100]">
              {activeResultViewOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <div>
                    <p className="font-medium leading-none">{option.label}</p>
                    <p className="text-xs text-muted-foreground">{option.description}</p>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {activePollType === "constant_sum_two_level" && (
          <PollConstantSumEditor
            clusters={clusters}
            hideZeroAllocated={hideZeroAllocated}
            setHideZeroAllocated={setHideZeroAllocated}
            addCluster={addCluster}
            removeCluster={removeCluster}
            updateCluster={updateCluster}
            addSubCategory={addSubCategory}
            removeSubCategory={removeSubCategory}
            updateSubCategory={updateSubCategory}
            handleImageUpload={handleImageUpload}
            handleRemoveImage={handleRemoveImage}
          />
        )}

        {(activePollType === "ranking" || activePollType === "ranking_minimum") && (
          <PollRankingEditor
            pollType={activePollType}
            rankingOptions={rankingOptions}
            minimumRequiredRanks={minimumRequiredRanks}
            setMinimumRequiredRanks={setMinimumRequiredRanks}
            addRankingOption={addRankingOption}
            moveRankingOption={moveRankingOption}
            removeRankingOption={removeRankingOption}
            updateRankingOption={updateRankingOption}
            handleRankingImageUpload={handleRankingImageUpload}
            handleRankingRemoveImage={handleRankingRemoveImage}
          />
        )}

        <AlertDialog open={showWipeDialog} onOpenChange={setShowWipeDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Wipe Poll Data?</AlertDialogTitle>
              <AlertDialogDescription>
                {responseCount > 0
                  ? `This poll currently has ${responseCount} response(s). Wiping will delete only the responses and keep your poll setup.`
                  : "There are no responses yet. Wiping now will delete the entire poll setup."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleWipePollData} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                {responseCount > 0 ? "Wipe Responses" : "Wipe Poll Setup"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
});

PollEditor.displayName = "PollEditor";
